import { useQuery } from "react-query";
import {
  getMyExecutiveAccess,
  getMyPortfolioViewerAccess,
  getMyPracticeViewerAccess,
} from "apps/project-iq/api";

export const MY_PORTFOLIO_VIEWER_ACCESS_QUERY_KEY = "myPortfolioViewerAccess";
export const MY_PRACTICE_VIEWER_ACCESS_QUERY_KEY = "myPracticeViewerAccess";
export const MY_EXECUTIVE_ACCESS_QUERY_KEY = "myExecutiveAccess";

export interface IUseLeadershipDashboardAccessResult {
  hasPortfolioAccess: boolean;
  hasPracticeAccess: boolean;
  hasExecutiveAccess: boolean;
  hasLeadershipDashboardAccess: boolean;
  portfolioIds: string[];
  practiceIds: string[];
  isLoading: boolean;
  isPortfolioAccessError: boolean;
  isPracticeAccessError: boolean;
  isExecutiveAccessError: boolean;
}

export const useLeadershipDashboardAccess =
  (): IUseLeadershipDashboardAccessResult => {
    const {
      data: portfolioAccess,
      isLoading: isPortfolioAccessLoading,
      isError: isPortfolioAccessError,
    } = useQuery<{ portfolioIds: string[] }, Error>({
      queryKey: [MY_PORTFOLIO_VIEWER_ACCESS_QUERY_KEY],
      queryFn: () => getMyPortfolioViewerAccess(),
      refetchOnWindowFocus: false,
    });

    const {
      data: practiceAccess,
      isLoading: isPracticeAccessLoading,
      isError: isPracticeAccessError,
    } = useQuery<{ practiceIds: string[] }, Error>({
      queryKey: [MY_PRACTICE_VIEWER_ACCESS_QUERY_KEY],
      queryFn: () => getMyPracticeViewerAccess(),
      refetchOnWindowFocus: false,
    });

    const {
      data: executiveAccess,
      isLoading: isExecutiveAccessLoading,
      isError: isExecutiveAccessError,
    } = useQuery<{ hasExecutiveAccess: boolean }, Error>({
      queryKey: [MY_EXECUTIVE_ACCESS_QUERY_KEY],
      queryFn: () => getMyExecutiveAccess(),
      refetchOnWindowFocus: false,
    });

    const portfolioIds = portfolioAccess?.portfolioIds ?? [];
    const practiceIds = practiceAccess?.practiceIds ?? [];
    const hasExecutiveAccess = executiveAccess?.hasExecutiveAccess ?? false;
    const hasPortfolioAccess = portfolioIds.length > 0 || hasExecutiveAccess;
    const hasPracticeAccess = practiceIds.length > 0 || hasExecutiveAccess;

    return {
      hasPortfolioAccess,
      hasPracticeAccess,
      hasExecutiveAccess,
      hasLeadershipDashboardAccess: hasPortfolioAccess || hasPracticeAccess,
      portfolioIds,
      practiceIds,
      isLoading:
        isPortfolioAccessLoading ||
        isPracticeAccessLoading ||
        isExecutiveAccessLoading,
      isPortfolioAccessError,
      isPracticeAccessError,
      isExecutiveAccessError,
    };
  };
